import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import {
  Assessment_Activity_Status_Enum,
  Assessment_Status_Enum,
  Issue_Assessment_Status_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';

import { useGetTypes } from './config';

export const useGetDefaultRibbonFilters = (): FilterModal[] => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'dashboard.myItemsDashboard.ribbonTitles',
  });
  const types = useGetTypes();

  const { getLabel: getActionLabel } = useRating('action_status');
  const { getLabel: getActivityLabel } = useRating(
    'assessment_activity_status'
  );
  const { getLabel: getAssessmentLabel } = useRating('assessment_status');
  const { getLabel: getIssueLabel } = useRating('issue_assessment_status');
  const { getByValue: getCommonLookupByValue } = useCommonLookupLazy();

  return useMemo(
    () => [
      {
        id: uuidv4(),
        title: t('actions'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.action,
            },
            // Matches the pre-refactor tile behaviour: show everything that
            // isn't Closed, including Open, Pending, and Overdue.
            {
              operator: '!=',
              propertyKey: 'StatusLabelled',
              value: getActionLabel('closed'),
            },
          ],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('risks'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.risk,
            },
            {
              operator: '!=',
              propertyKey: 'StatusLabelled',
              // Use the same lookup that useFlattenRibbonData uses for risk StatusLabelled
              value:
                getCommonLookupByValue('statuses', 'retired')?.label ??
                'Retired',
            },
          ],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('rcsaActivities'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.RCSAActivity,
            },
            {
              operator: '!=',
              propertyKey: 'StatusLabelled',
              value: getActivityLabel(Assessment_Activity_Status_Enum.Complete),
            },
          ],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('indicators'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.indicator,
            },
          ],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('documents'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.document,
            },
          ],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('assessments'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.assessment,
            },
            {
              operator: '!=',
              propertyKey: 'StatusLabelled',
              value: getAssessmentLabel(Assessment_Status_Enum.Complete),
            },
          ],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('controls'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.control,
            },
          ],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('issues'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.issue,
            },
            // Matches the pre-refactor tile behaviour: show everything that
            // isn't Closed (Pending / AwaitingClosure / FirstLineApproval /
            // Declined all remain visible).
            {
              operator: '!=',
              propertyKey: 'StatusLabelled',
              value: getIssueLabel(Issue_Assessment_Status_Enum.Closed),
            },
          ],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('obligations'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.obligationReview,
            },
          ],
          operation: 'and',
        },
      },
    ],
    [
      t,
      types,
      getActionLabel,
      getActivityLabel,
      getAssessmentLabel,
      getIssueLabel,
      getCommonLookupByValue,
    ]
  );
};
